import React, { useState, useEffect } from 'react';
import { ArrowRight, AlertCircle, QrCode, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';
import { Tooltip } from '../components/Tooltip';
import QrReader from 'react-qr-scanner';

function AddCustomer() {
  const navigate = useNavigate();
  const [customerSmartKey, setCustomerSmartKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [vendorProfile, setVendorProfile] = useState<Profile | null>(null);
  const [searchResults, setSearchResults] = useState<Profile[]>([]);
  const [searching, setSearching] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);

  useEffect(() => {
    fetchVendorProfile();
  }, []);

  const fetchVendorProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile) throw new Error('No profile found');
      if (profile.account_type !== 'shop_owner') {
        navigate('/home');
        return;
      }

      setVendorProfile(profile);
    } catch (err) {
      console.error('Error fetching vendor profile:', err);
      navigate('/home');
    }
  };

  const handleSmartKeySearch = async (value: string) => {
    const searchValue = value.toUpperCase();
    setCustomerSmartKey(searchValue);
    setError(null);
    
    if (searchValue.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('smart_key, username')
        .ilike('smart_key', `${searchValue}%`)
        .limit(5);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err) {
      console.error('Error searching smart keys:', err);
      setError('Failed to search for customers');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      const { data, error } = await supabase
        .rpc('create_vendor_customer_relationship', {
          p_customer_smart_key: customerSmartKey.trim().toUpperCase()
        });

      if (error) throw error;

      setSuccess(true);
      setCustomerSmartKey('');
      setSearchResults([]);
      setShowQrScanner(false);
    } catch (err) {
      console.error('Error adding customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to add customer');
    } finally {
      setLoading(false);
    }
  };

  const handleQrScan = (result: { text: string } | null) => {
    if (result?.text) {
      setCustomerSmartKey(result.text.toUpperCase());
      setShowQrScanner(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-white/50"
        >
          <ArrowRight size={24} className="text-gray-800" />
        </button>
        <div className="flex items-center space-x-2">
          <h1 className="text-xl font-semibold">إضافة عميل</h1>
          <Tooltip text="إضافة عميل جديد لمتجرك. يمكنك إضافة العميل باستخدام الرمز الذكي الخاص به." />
        </div>
      </div>

      <div className="w-full max-w-sm mx-auto bg-white rounded-3xl shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">عميل جديد</h2>
          <div className="flex items-center justify-center space-x-2">
            <p className="text-gray-600">أضف عميلاً باستخدام الرمز الذكي</p>
            <Tooltip text="الرمز الذكي هو رمز فريد مكون من 6 أحرف يتم إنشاؤه تلقائياً لكل مستخدم" />
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 rounded-xl flex items-center space-x-2 text-red-600">
            <AlertCircle size={20} />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 rounded-xl text-green-600 text-sm">
            تمت إضافة العميل بنجاح!
          </div>
        )}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="relative">
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="الرمز الذكي للعميل"
                value={customerSmartKey}
                onChange={(e) => handleSmartKeySearch(e.target.value)}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 uppercase"
                maxLength={6}
                pattern="[A-Z0-9]{6}"
                title="يجب أن يكون الرمز الذكي 6 أحرف"
                required
              />
              <button
                type="button"
                onClick={() => setShowQrScanner(!showQrScanner)}
                className="px-4 py-3 bg-blue-100 text-blue-600 rounded-xl hover:bg-blue-200 transition-colors"
              >
                <QrCode size={20} />
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              أدخل الرمز الذكي المكون من 6 أحرف المقدم من العميل أو امسح رمز QR
            </p>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                {searchResults.map((result) => (
                  <button
                    key={result.smart_key}
                    type="button"
                    onClick={() => {
                      setCustomerSmartKey(result.smart_key);
                      setSearchResults([]);
                    }}
                    className="w-full px-4 py-3 text-right hover:bg-gray-50 flex justify-between items-center"
                  >
                    <span className="font-medium">{result.smart_key}</span>
                    <span className="text-sm text-gray-500">{result.username}</span>
                  </button>
                ))}
              </div>
            )}

            {searching && (
              <div className="absolute right-3 top-3">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          {showQrScanner && (
            <div className="relative">
              <div className="absolute -top-2 right-2 z-10">
                <button
                  type="button"
                  onClick={() => setShowQrScanner(false)}
                  className="p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="rounded-xl overflow-hidden">
                <QrReader
                  onError={(err) => console.error(err)}
                  onScan={handleQrScan}
                  style={{ width: '100%' }}
                  facingMode="environment" // This forces the back camera
                  delay={1000}
                  constraints={{
                    video: { facingMode: "environment" }
                  }}
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !customerSmartKey || customerSmartKey.length !== 6}
            className="w-full py-3 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'جاري إضافة العميل...' : 'إضافة العميل'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddCustomer;